// Sign-in, in two steps.
//
// A correct password never produces a session on its own: two-step
// verification is required, so the password screen hands over to either the
// code screen or compulsory enrolment.

import { useState, type FormEvent } from 'react';
import { api, type EnrolmentOffer } from '../lib/api';
import { fieldErrors, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Icon } from '../components/Icon';
import { RecoveryCodes, TwoFactorSetup } from '../components/TwoFactorSetup';
import { ErrorAlert, TextField } from '../components/ui';
import { LOCALES, type Locale } from '@shared/types';

const LOCALE_LABELS: Record<Locale, string> = { es: 'Español', en: 'English' };

type Step =
  | { name: 'password' }
  | { name: 'totp' }
  | { name: 'enrol'; offer: EnrolmentOffer }
  | { name: 'recovery-codes'; codes: string[] };

export function Login() {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const { signIn, completeSignIn } = useSession();
  const toMessage = useErrorMessage();

  const [step, setStep] = useState<Step>({ name: 'password' });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onPasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const stage = await signIn(username.trim(), password);
      if (stage === 'totp') {
        setStep({ name: 'totp' });
      } else {
        setStep({ name: 'enrol', offer: await api.beginEnrolment() });
      }
      // The password is not needed again; don't keep it in memory.
      setPassword('');
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <img src="/favicon.svg" alt="" />
          ARES Invoice
        </div>
        <p className="login__tagline">
          {step.name === 'password' ? t('app.tagline') : t('twofactor.title')}
        </p>

        <div className="card">
          <div className="card__body">
            {step.name === 'password' && (
              <form className="login__form" onSubmit={onPasswordSubmit} noValidate>
                {error && <ErrorAlert message={error} />}
                <TextField
                  label={t('login.username')} name="username" autoComplete="username"
                  autoFocus required value={username}
                  onChange={(e) => setUsername(e.target.value)} />
                <TextField
                  label={t('login.password')} name="password" type="password"
                  autoComplete="current-password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} />
                <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
                  {busy && <span className="spinner" />}
                  {busy ? t('login.signingIn') : t('login.submit')}
                </button>
              </form>
            )}

            {step.name === 'totp' && (
              <CodeStep
                onVerified={() => void completeSignIn()}
                onBack={() => { setStep({ name: 'password' }); setError(undefined); }}
              />
            )}

            {step.name === 'enrol' && (
              <>
                <div className="alert alert--info" style={{ marginBottom: 14 }}>
                  <Icon name="shield" />
                  <div>{t('twofactor.required')}</div>
                </div>
                <TwoFactorSetup
                  offer={step.offer}
                  onConfirm={async (code) => {
                    const result = await api.confirmEnrolment(code);
                    setStep({ name: 'recovery-codes', codes: result.recoveryCodes });
                  }}
                />
              </>
            )}

            {step.name === 'recovery-codes' && (
              <>
                <h2 className="card__title" style={{ marginBottom: 10 }}>
                  {t('twofactor.recoveryTitle')}
                </h2>
                <RecoveryCodes codes={step.codes} onAcknowledge={() => void completeSignIn()} />
              </>
            )}
          </div>
        </div>

        {step.name === 'password' && (
          <div className="login__footer btn-group">
            {LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                className={`btn btn--sm${code === locale ? ' btn--primary' : ' btn--ghost'}`}
                onClick={() => setLocale(code)}
                aria-pressed={code === locale}
              >
                {LOCALE_LABELS[code]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** The code step: an authenticator code, or a recovery code instead. */
function CodeStep({ onVerified, onBack }: { onVerified: () => void; onBack: () => void }) {
  const t = useT();
  const toMessage = useErrorMessage();
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.verifyTotp(code.trim());
      onVerified();
    } catch (err) {
      setError(fieldErrors(err).code ?? toMessage(err));
      setBusy(false);
      setCode('');
    }
  }

  const isTotp = mode === 'totp';

  return (
    <form className="login__form" onSubmit={onSubmit} noValidate>
      {error && <ErrorAlert message={error} />}
      <p className="muted" style={{ marginTop: -2 }}>
        {isTotp ? t('twofactor.challengeIntro') : t('twofactor.recoveryHint')}
      </p>

      <div className={isTotp ? 'code-input' : undefined}>
        <TextField
          label={isTotp ? t('twofactor.code') : t('twofactor.recoveryCode')}
          inputMode={isTotp ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          placeholder={isTotp ? t('twofactor.codePlaceholder') : t('twofactor.recoveryPlaceholder')}
          maxLength={isTotp ? 7 : 16}
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>

      <button type="submit" className="btn btn--primary btn--block" disabled={busy || code.trim().length < 6}>
        {busy && <span className="spinner" />}
        {busy ? t('twofactor.verifying') : t('twofactor.verify')}
      </button>

      <div className="row row--between">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>
          <Icon name="arrow-left" size={14} />{t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => { setMode(isTotp ? 'recovery' : 'totp'); setCode(''); setError(undefined); }}
        >
          {isTotp ? t('twofactor.useRecovery') : t('twofactor.useAuthenticator')}
        </button>
      </div>
    </form>
  );
}
